import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import format from 'string-template';
import { Alert, LinearProgress } from '@mui/material';

import useRunWatchdog from '../../../hooks/useRunWatchdog';
import WebflowDeploy, { IN_PROGRESS_STATUSES, WebflowDeployStatus } from '../../../Model/WebflowDeploy';
import { DeployDisplayStatus, deriveDeployStatus } from '../../../utils/webflowDeployer';
import { DEPLOY_PHASES, ItemPhase } from '../hooks/useDeployQueue';
import texts from '../texts.json';
import {
  ItemHeader,
  Meta,
  SiteLink,
} from '../styles';
import DeployStatusTag from './DeployStatusTag';
import ErrorDetails from './ErrorDetails';
import MessageAlert from './MessageAlert';

const DeployedLink = ({ url, siteName }) => (
  <Alert severity="success">
    {format(texts.item.deployedTo, { site: siteName })}
    {' — '}
    <SiteLink href={url} target="_blank" rel="noopener noreferrer">{url}</SiteLink>
  </Alert>
);

DeployedLink.propTypes = {
  url: PropTypes.string.isRequired,
  siteName: PropTypes.string.isRequired,
};

/**
 * A deploy's progress and outcome, read off webflowDeploys/{runId} as the endpoint writes it.
 * The response settles a deploy whose request came back; the document settles one whose request
 * died, including one the platform killed, which the deadline gives away.
 */
const DeployProgress = ({
  runId,
  storagePath,
  phase,
  result,
  deployError,
}) => {
  // Observed rather than fetched: rendering it under `observer` keeps the listener attached.
  const deployDoc = useMemo(() => new WebflowDeploy(runId), [runId]);
  const hasDoc = deployDoc.exists;
  const isFollowing = phase === ItemPhase.FOLLOWING;
  /*
    The deadline is watched for every deploy shown here, so one the platform kills turns TIMED_OUT.
    A missing document only counts once the request has died (FOLLOWING): until then the response
    is still to come, so the grace period must not start running.
  */
  const isAwaitingDoc = isFollowing && !hasDoc;
  const { now, isMissing } = useRunWatchdog({
    isWatching: true,
    isFinished: hasDoc && !IN_PROGRESS_STATUSES.includes(deployDoc.status),
    hasDoc: !isAwaitingDoc,
    deadlineMs: hasDoc ? deployDoc.deadlineAt?.getTime() : undefined,
  });
  const status = deriveDeployStatus(hasDoc ? deployDoc : null, now);

  const warnings = hasDoc ? deployDoc.warnings : [];
  const details = [
    hasDoc ? deployDoc.error : '',
    deployError,
    `runId: ${runId}`,
    `storagePath: ${storagePath}`,
  ];

  const renderOutcome = () => {
    if (phase === ItemPhase.DEPLOYED) {
      return <DeployedLink url={result.url} siteName={result.siteName} />;
    }
    if (status === DeployDisplayStatus.COMPLETED) {
      return <DeployedLink url={deployDoc.url} siteName={deployDoc.netlifySiteName} />;
    }
    if (status === DeployDisplayStatus.FAILED) {
      return <ErrorDetails message={deployDoc.error || deployError || texts.deployFailed} details={details} />;
    }
    if (status === DeployDisplayStatus.TIMED_OUT) {
      return <ErrorDetails message={texts.item.timedOut} details={details} />;
    }
    if (isMissing) {
      // The request's own error (always set by now) goes in the details: it is why we followed.
      return <ErrorDetails message={texts.item.neverStarted} details={details} />;
    }

    const progressStatus = hasDoc ? deployDoc.status : DeployDisplayStatus.PENDING;
    const isCountingFiles = progressStatus === WebflowDeployStatus.UPLOADING && deployDoc.total > 0;
    return (
      <>
        <Meta>
          {format(texts.progress[progressStatus] || texts.status.IN_PROGRESS, {
            uploaded: deployDoc.uploaded,
            total: deployDoc.total,
          })}
        </Meta>
        <LinearProgress
          variant={isCountingFiles ? 'determinate' : 'indeterminate'}
          value={isCountingFiles ? (deployDoc.uploaded / deployDoc.total) * 100 : 0}
        />
        {isFollowing && <Meta>{texts.item.followingNote}</Meta>}
      </>
    );
  };

  const displayStatus = phase === ItemPhase.DEPLOYED ? DeployDisplayStatus.COMPLETED : status;

  return (
    <>
      <ItemHeader>
        <DeployStatusTag status={displayStatus} />
      </ItemHeader>
      {renderOutcome()}
      <MessageAlert severity="warning" title={texts.item.warnings} messages={warnings} />
    </>
  );
};

DeployProgress.propTypes = {
  runId: PropTypes.string.isRequired,
  storagePath: PropTypes.string.isRequired,
  phase: PropTypes.oneOf(DEPLOY_PHASES).isRequired,
  result: PropTypes.shape({
    url: PropTypes.string,
    siteName: PropTypes.string,
  }),
  deployError: PropTypes.string,
};

DeployProgress.defaultProps = {
  result: null,
  deployError: '',
};

export default observer(DeployProgress);
